/**
 * BundleDetailPage - Page de détail d'un bundle/pack
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import { 
  Package, 
  ShoppingCart, 
  Check, 
  ArrowLeft,
  Tag,
  Loader2,
  ChevronRight,
  Home
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BundleDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { addBundleToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const lang = i18n.language;

  useEffect(() => {
    const fetchBundle = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/promotions/bundles/${id}`);
        setBundle(res.data);
      } catch (err) {
        console.error('Error fetching bundle:', err);
        toast.error(t('bundle.notFound', 'Pack non trouvé'));
      } finally {
        setLoading(false);
      }
    };

    fetchBundle();
  }, [id, t]);

  const handleAddToCart = async () => {
    if (!bundle) return;
    
    setAdding(true);
    try {
      // Ajouter tous les produits du bundle au panier
      if (addBundleToCart) {
        await addBundleToCart(bundle);
      } else {
        // Fallback: ajouter chaque produit individuellement
        for (const product of bundle.products || []) {
          await axios.post(`${API_URL}/api/cart/items`, {
            product_id: product.id,
            quantity: 1,
            metadata: { from_bundle: bundle.id, bundle_name: bundle.name?.[lang] }
          });
        }
      }
      toast.success(t('bundle.addedToCart', 'Pack ajouté au panier'));
    } catch (err) {
      console.error('Error adding bundle to cart:', err);
      toast.error(t('bundle.addError', 'Erreur lors de l\'ajout'));
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-mutagim-dark">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="min-h-screen flex flex-col bg-mutagim-dark">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center gap-4">
          <Package className="w-16 h-16 text-gray-600" />
          <p className="text-gray-400">{t('bundle.notFound', 'Pack non trouvé')}</p>
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.backToHome', 'Retour à l\'accueil')}
            </Button>
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const name = bundle.name?.[lang] || bundle.name?.fr || 'Pack';
  const description = bundle.description?.[lang] || bundle.description?.fr || '';
  const discount = bundle.original_price > 0 
    ? Math.round((1 - bundle.bundle_price / bundle.original_price) * 100) 
    : 0;
  const savings = bundle.original_price - bundle.bundle_price;

  return (
    <div className="min-h-screen flex flex-col bg-mutagim-dark">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto px-4 py-8 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
            <Home className="h-4 w-4" />
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-white font-medium">{name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Images des produits */}
          <div className="space-y-4">
            <div className={cn(
              "relative rounded-2xl overflow-hidden border-2",
              "bg-gradient-to-br from-purple-900/30 to-zinc-900",
              "border-purple-500/30"
            )}>
              {/* Badge */}
              <div className="absolute top-4 left-4 z-10 bg-purple-600 text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-2">
                <Package className="w-4 h-4" />
                PACK EXCLUSIF
              </div>
              
              {discount > 0 && (
                <div className="absolute top-4 right-4 z-10 bg-gradient-to-r from-red-600 to-red-500 text-white text-lg font-bold px-4 py-2 rounded-full">
                  -{discount}%
                </div>
              )}

              {/* Grid des produits */}
              <div className="grid grid-cols-2 gap-1 p-2">
                {(bundle.products || []).slice(0, 4).map((product, idx) => {
                  const imgUrl = product.images?.main?.[0];
                  const fullUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                  
                  return (
                    <div key={product.id || idx} className="aspect-square rounded-lg overflow-hidden bg-zinc-800">
                      {fullUrl ? (
                        <img 
                          src={fullUrl} 
                          alt={product.name?.[lang] || ''} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-zinc-600" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Détails */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-white">{name}</h1>
              {description && (
                <p className="text-gray-400 mt-2">{description}</p>
              )}
            </div>

            {/* Prix */}
            <div className="bg-zinc-900/80 rounded-xl p-6 border border-zinc-800">
              <div className="flex items-end gap-4">
                <span className="text-4xl font-bold text-white">
                  {formatPrice(bundle.bundle_price)}
                </span>
                {bundle.original_price > bundle.bundle_price && (
                  <span className="text-2xl text-gray-500 line-through">
                    {formatPrice(bundle.original_price)}
                  </span>
                )}
              </div>
              
              {savings > 0 && (
                <p className="text-lg text-green-400 mt-2 flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  {t('bundle.youSave', 'Vous économisez')} {formatPrice(savings)}
                </p>
              )}
            </div>

            {/* Produits inclus */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                {t('bundle.includedProducts', 'Produits inclus')} ({bundle.products?.length || 0})
              </h3>
              
              <div className="space-y-3">
                {(bundle.products || []).map((product, idx) => {
                  const imgUrl = product.images?.main?.[0];
                  const fullUrl = imgUrl?.startsWith('/api/') ? `${API_URL}${imgUrl}` : imgUrl;
                  const productName = product.name?.[lang] || product.name?.fr || `Produit ${idx + 1}`;
                  const productPrice = product.price?.amount || product.pricing?.prices?.ILS || 0;
                  
                  return (
                    <Link 
                      key={product.id || idx}
                      to={`/product/${product.slug}`}
                      className="flex items-center gap-4 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800 hover:border-purple-500/50 transition-colors group"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                        {fullUrl ? (
                          <img src={fullUrl} alt={productName} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-zinc-600" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate group-hover:text-purple-400 transition-colors">
                          {productName}
                        </p>
                        <p className="text-sm text-gray-400">
                          {t('bundle.valueOf', 'Valeur')} {formatPrice(productPrice)}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Bouton d'achat */}
            <Button 
              onClick={handleAddToCart}
              disabled={adding}
              className="w-full h-14 text-lg bg-purple-600 hover:bg-purple-700 text-white"
            >
              {adding ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ShoppingCart className="w-5 h-5 mr-2" />
              )}
              {t('bundle.addToCart', 'Ajouter le pack au panier')}
            </Button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

export default BundleDetailPage;
